import { useRef } from "react";
import { Icon } from "@common/index";
import { useDialogFocusTrap } from "@/hooks/useDialogFocusTrap";

interface BottomSheetProps {
    title: string;
    onClose: () => void;
    onApply: () => void;
    /** Optional override for the Apply button label. */
    applyLabel?: string;
    /** Data-testid forwarded to the outer aside so callers can scope queries. */
    testId?: string;
    children: React.ReactNode;
}

// Shared mobile-only bottom sheet used by SortSheet and FilterSheet, on the
// shared useDialogFocusTrap chassis. The caller controls open state externally
// (via atom) and conditionally renders this component when isOpen is true;
// that way each open mounts a fresh component instance and any local sheet
// state is reset to URL-derived values.
export const BottomSheet = ({
    title,
    onClose,
    onApply,
    applyLabel = "Apply",
    testId,
    children,
}: BottomSheetProps) => {
    const sheetRef = useRef<HTMLElement>(null);

    // The hook moves focus once per open, never per render: a focus move that re-ran with
    // `onClose` pulled focus back to Close on every buffered edit, and the price slider could
    // not be driven by keyboard (#226 review F5). A chip can unmount under focus — an off-list
    // selection toggled off in the draft — so the trap must survive focus falling to <body>.
    // The backdrop below is a sibling of the sheet, so a drag out of the sheet never clicks it:
    // its plain onClick stays.
    const { panelProps } = useDialogFocusTrap({
        panelRef: sheetRef,
        isProcessing: false,
        onRequestClose: onClose,
    });

    return (
        <>
            <div
                onClick={onClose}
                className="fixed inset-0 bg-black/30 z-40 md:hidden"
            />
            <aside
                ref={sheetRef}
                data-testid={testId}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                {...panelProps}
                className="fixed inset-x-0 bottom-0 z-50 bg-base-100 text-neutral rounded-t-2xl shadow-2xl md:hidden flex flex-col max-h-[85vh] focus:outline-none"
            >
                <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-base-200">
                    <button
                        type="button"
                        aria-label="Close"
                        onClick={onClose}
                        className="btn btn-ghost btn-sm btn-square text-neutral"
                    >
                        <Icon name="close" className="text-2xl" />
                    </button>
                    <span className="font-bold text-neutral">{title}</span>
                    <button
                        type="button"
                        onClick={onApply}
                        className="btn btn-secondary btn-sm font-bold"
                    >
                        {applyLabel}
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto">{children}</div>
            </aside>
        </>
    );
};
